import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { useSettings } from "@/context/SettingsContext";
import { resolveBaseLayers, tileSubdomains } from "@/lib/mapTiles";

const LABELS = { dark: "Ciemna", osm: "Drogowa", sat: "Satelita" };

const pin = L.divIcon({
  className: "",
  html: `<div style="width:28px;height:28px;transform:translateY(-6px)"><svg viewBox="0 0 24 24" width="28" height="28" fill="#06B6D4" stroke="#0F172A" stroke-width="1.5"><path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="#0F172A"/></svg></div>`,
  iconSize: [28, 28], iconAnchor: [14, 22],
});

function Clicker({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function Controller({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    // Fix gray tiles when map mounts inside a modal (size computed before layout)
    const t = setTimeout(() => map.invalidateSize(), 200);
    // Cancel any pending zoom/pan animation before the map unmounts (avoids
    // Leaflet "_leaflet_pos" TypeError when the modal closes mid-transition).
    return () => { clearTimeout(t); try { map.stop(); } catch { /* map already removed */ } };
  }, [map]);
  useEffect(() => {
    if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      map.setView([lat, lng], Math.max(map.getZoom(), 14), { animate: false });
    }
  }, [lat, lng, map]);
  return null;
}

export default function MiniMap({ lat, lng, onPick, defaultCenter }) {
  const { settings } = useSettings();
  const layers = resolveBaseLayers(settings);
  const [base, setBase] = useState("osm");
  const b = layers[base] || layers.osm;
  const valid = lat !== "" && lng !== "" && lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
  const dcOk = Array.isArray(defaultCenter) && defaultCenter.length === 2 &&
    Number.isFinite(defaultCenter[0]) && Number.isFinite(defaultCenter[1]) &&
    !(defaultCenter[0] === 0 && defaultCenter[1] === 0);
  const fallback = dcOk ? defaultCenter : [52.0693, 19.4803];
  const center = valid ? [Number(lat), Number(lng)] : fallback;
  const cls = base === "dark" ? "tiles-dark" : "";
  return (
    <div className="relative rounded-md overflow-hidden border border-slate-700" style={{ height: 190 }}>
      <div className={`w-full h-full ${cls}`}>
        <MapContainer center={center} zoom={valid ? 14 : 12} zoomControl={true} className="w-full h-full" style={{ background: "#0b1120" }} data-testid="minimap">
          <TileLayer url={b.url} subdomains={tileSubdomains(b.url)} maxZoom={19} />
          <Clicker onPick={onPick} />
          <Controller lat={valid ? Number(lat) : null} lng={valid ? Number(lng) : null} />
          {valid && <Marker position={[Number(lat), Number(lng)]} icon={pin} />}
        </MapContainer>
      </div>
      <div className="absolute top-1.5 right-1.5 z-[500] flex rounded-md overflow-hidden border border-slate-700 backdrop-blur-md bg-slate-900/85">
        {Object.keys(layers).map((k) => (
          <button key={k} type="button" onClick={() => setBase(k)} data-testid={`minimap-base-${k}`}
            className={`px-2 py-1 text-[10px] ${base === k ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-700"}`}>
            {LABELS[k] || k}
          </button>
        ))}
      </div>
    </div>
  );
}
